import React, { useState, useEffect } from "react"
import { useForm } from "react-hook-form"

import * as Yup from "yup"

import { yupResolver } from "@hookform/resolvers/yup"
import { colors, IconErrorMedium } from "@izettle/otto"
import { useLocalization } from "@izettle/tenko-react"

import {
  Content,
  Footer,
  Button,
  LargeInput,
  Container,
  ErrorSpan,
  Form,
  SvgIcon,
  Flex,
} from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import {
  useSignUpTracking,
  TrackingAction,
} from "@/modules/sign-up/hooks/tracking-sign-up"

import { SlideIndexs } from "./types"

const Error = ({ label }: { label: string | undefined }) => {
  return <ErrorSpan>{label}</ErrorSpan>
}

const getCurrentFeildForTracking = (index: SlideIndexs) => {
  let field = ""
  switch (index) {
    case SlideIndexs.FirstName:
      field = "FirstName"
      break
    case SlideIndexs.LastName:
      field = "LastName"
      break
    case SlideIndexs.Email:
      field = "EmailAddress"
      break
    default:
      break
  }

  return field
}

export type InputSlideProps = {
  purchaseUuid: string
  name: string
  type: string
  placeholder: string
  fieldMatch: RegExp
  fieldMatchMessage: string
  onConfirm?: (name: string, value: any, next: () => void) => void
  slideNumber?: number
  nextSlide: number
  confirmText?: string
  value?: string
}

export const InputSlide = ({
  purchaseUuid,
  placeholder,
  slideNumber,
  onConfirm,
  name,
  type,
  fieldMatch,
  fieldMatchMessage,
  nextSlide,
  confirmText,
  value,
}: InputSlideProps) => {
  const { text } = useLocalization()
  const { goTo, index } = useSlider()
  const { track } = useSignUpTracking(purchaseUuid)
  const [hasChange, setHasChange] = useState(false)
  const [hasRetunToEdit, setHasRetunToEdit] = useState(false)

  useEffect(() => {
    if (hasChange) {
      setHasRetunToEdit(true)
      setHasChange(false)
    }
  }, [index])

  useEffect(() => {
    if (hasChange) {
      track(hasRetunToEdit ? TrackingAction.EditedInfo : TrackingAction.EnteredInfo, {
        field: getCurrentFeildForTracking(index),
      })
    }
  }, [hasChange])

  const next = (params?: any) => goTo(nextSlide, params)

  const defaultValues = { [name]: "" }
  const schema = Yup.object().shape({
    [name]: Yup.string()
      .matches(fieldMatch, fieldMatchMessage)
      .required(text("SIGNUP_REQUIRED")),
  })

  const { handleSubmit, formState, setValue, control } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  })

  const onSubmit = (values: any) => {
    if (onConfirm) {
      onConfirm(name, values[name], next)
    }
  }

  const handleInputChange = (event: any) => {
    const newValue: string = event.target.value ?? ""
    setValue(name, newValue)
    setHasChange(true)
  }

  useEffect(() => {
    if (value) {
      setValue(name, value)
    }
  }, [value])

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Container>
        <Content>
          <LargeInput
            name={name}
            type={type}
            placeholder={placeholder}
            control={control}
            onChange={handleInputChange}
            ariaInvalid={!!formState.errors[name]}
          />
          {formState.errors[name] ? (
            <Flex>
              <SvgIcon
                html={IconErrorMedium.svg}
                color={colors.text.error}
                margin="0 16px 0 0"
              />
              <Error label={formState.errors[name]?.message} />
            </Flex>
          ) : null}
        </Content>
        <Footer>
          <Button
            size="large"
            type="submit"
            data-testid={`slide-${slideNumber}-submit-button`}
          >
            {confirmText ?? text("SIGNUP_NEXT")}
          </Button>
        </Footer>
      </Container>
    </Form>
  )
}
